/** Telas desktop de referência/leitura (tabelas densas). Mesma fonte de dados
 * das telas mobile, em layout largo. Reusam o kit desktop + engine + design-system. */
import {
  CATALOGO,
  DIMENSOES_IMS,
  ITENS_PERCEPCAO,
  MACROS,
  RISCOS_REFERENCIAIS,
  SETORES,
  SUP_CAMPO,
  SUP_CHECKLIST,
  SUP_PRE,
  VERSOES,
} from "@caint/data";
import { EixoChip, PesoChip } from "@caint/design-system";
import { useMemo, useState } from "react";
import { engine } from "../../lib/engine.ts";
import { Card, type Coluna, PageHeader, Tabela } from "../ui/kit.tsx";

/* ---------------- Catálogo (251 itens) ---------------- */
export function Catalogo() {
  const [busca, setBusca] = useState("");
  const [eixo, setEixo] = useState<"todos" | "IMGO" | "IMQA" | "IMGR">("todos");
  const linhas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return CATALOGO.filter(
      (it) =>
        (eixo === "todos" || it.eixo === eixo) &&
        (q === "" || it.pergunta.toLowerCase().includes(q) || it.code.toLowerCase().includes(q)),
    );
  }, [busca, eixo]);
  const colunas: Coluna<(typeof CATALOGO)[number]>[] = [
    { rotulo: "Código", largura: "90px", celula: (i) => <span className="chip chip-code">{i.code}</span> },
    { rotulo: "Peso", largura: "84px", celula: (i) => <PesoChip peso={i.peso} /> },
    { rotulo: "Eixo", largura: "72px", celula: (i) => <EixoChip eixo={i.eixo} /> },
    { rotulo: "Pergunta", celula: (i) => i.pergunta },
    { rotulo: "Norma", largura: "130px", celula: (i) => <span className="spec">{i.norma}</span> },
  ];
  return (
    <div className="dk-page">
      <PageHeader
        titulo="Catálogo"
        sub={`${linhas.length} de ${CATALOGO.length} itens do instrumento v4.0`}
      />
      <div className="dk-toolbar">
        <input
          className="m-input"
          placeholder="Buscar por código ou pergunta…"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          style={{ minWidth: 320 }}
        />
        <select className="m-select" value={eixo} onChange={(e) => setEixo(e.target.value as typeof eixo)}>
          <option value="todos">Todos os eixos</option>
          <option value="IMGO">IMGO</option>
          <option value="IMQA">IMQA</option>
          <option value="IMGR">IMGR</option>
        </select>
      </div>
      <Tabela colunas={colunas} linhas={linhas} chave={(i) => i.id} vazio="Nenhum item encontrado." />
    </div>
  );
}

/* ---------------- Matriz de Riscos ---------------- */
export function Matriz() {
  const colunas: Coluna<(typeof RISCOS_REFERENCIAIS)[number]>[] = [
    { rotulo: "ID", largura: "90px", celula: (r) => <span className="chip chip-code">{r.id}</span> },
    { rotulo: "Categoria", largura: "130px", celula: (r) => <span className="spec">{r.categoria}</span> },
    { rotulo: "Processo", largura: "180px", celula: (r) => r.processo },
    { rotulo: "Evento de risco", celula: (r) => r.evento },
    { rotulo: "P×I", largura: "64px", num: true, celula: (r) => r.p * r.i },
  ];
  return (
    <div className="dk-page">
      <PageHeader
        titulo="Matriz de Riscos"
        sub={`${RISCOS_REFERENCIAIS.length} riscos referenciais (COSO)`}
      />
      <Tabela colunas={colunas} linhas={RISCOS_REFERENCIAIS} chave={(r) => r.id} />
    </div>
  );
}

/* ---------------- Escalas & Selos ---------------- */
export function Escalas() {
  const c = engine.config;
  return (
    <div className="dk-page">
      <PageHeader titulo="Escalas & Selos" sub="Coeficientes vigentes (v4.0)" />
      <div className="dk-grid" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        <Card titulo="Curva ONA (score)">
          {Object.entries(c.curvaScore).map(([nivel, score]) => (
            <div key={nivel} className="dk-between" style={{ padding: "6px 0" }}>
              <span>
                Nível {nivel} — {engine.situacao(nivel)}
              </span>
              <b className="dk-num">{engine.num2(score)}</b>
            </div>
          ))}
        </Card>
        <Card titulo="Selos QualiEMSERH">
          {[
            ["🥇 Ouro", c.seloOuro],
            ["🥈 Prata", c.seloPrata],
            ["🥉 Bronze", c.seloBronze],
          ].map(([nome, lim]) => (
            <div key={nome as string} className="dk-between" style={{ padding: "6px 0" }}>
              <span>{nome}</span>
              <b className="dk-num">IMGQ ≥ {engine.pct(lim as number)}</b>
            </div>
          ))}
        </Card>
        <Card titulo="Níveis de risco (P×I)">
          {[
            ["RP · Pequeno", `≤ ${c.riskRP}`],
            ["RM · Médio", `≤ ${c.riskRM}`],
            ["RA · Alto", `≤ ${c.riskRA}`],
            ["RC · Crítico", `> ${c.riskRA}`],
          ].map(([nome, lim]) => (
            <div key={nome} className="dk-between" style={{ padding: "6px 0" }}>
              <span>{nome}</span>
              <b className="dk-num">{lim}</b>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

/* ---------------- Motor de Cálculo ---------------- */
export function Motor() {
  const c = engine.config;
  const linhas: [string, string][] = [
    ["Ponderação do IMGQ", c.ponderacao === "ponderadaPorItens" ? "Por nº de itens" : "Média simples"],
    ["Meta por subíndice", engine.pct(c.metaSubindice)],
    ["Redução máx. do controle", engine.pct(c.residualReducao)],
    ["Piso do risco residual", engine.pct(c.residualPiso)],
    ["Teto na exclusão CORE", engine.pct(c.tetoExclusao)],
    ["Gatilho de priorização do Plano de Ação", `risco ≥ ${c.gatilhoPlano}`],
    ["Faixa QACI", `± ${engine.pct(c.qaciFaixa)}`],
    ["Faixa IMS", `± ${engine.pct(c.imsFaixa)}`],
  ];
  return (
    <div className="dk-page">
      <PageHeader titulo="Motor de Cálculo" sub="Coeficientes vigentes (somente leitura)" />
      <Card>
        {linhas.map(([k, v]) => (
          <div
            key={k}
            className="dk-between"
            style={{ padding: "9px 0", borderBottom: "1px solid var(--slate-100)" }}
          >
            <span style={{ color: "var(--text-muted)" }}>{k}</span>
            <b className="dk-num">{v}</b>
          </div>
        ))}
      </Card>
    </div>
  );
}

/* ---------------- Estrutura ---------------- */
export function Estrutura() {
  const colunas: Coluna<(typeof SETORES)[number]>[] = [
    { rotulo: "Setor", celula: (s) => <b>{s.nome}</b> },
    { rotulo: "Código", largura: "90px", celula: (s) => <span className="spec">{s.code}</span> },
    { rotulo: "Macro", largura: "90px", celula: (s) => s.macro },
    { rotulo: "Itens", largura: "72px", num: true, celula: (s) => s.itens },
  ];
  return (
    <div className="dk-page">
      <PageHeader
        titulo="Estrutura"
        sub={`${SETORES.length} setores · ${CATALOGO.length} itens · 5 macroprocessos`}
      />
      <Card titulo="Macroprocessos" style={{ marginBottom: 16 }}>
        {Object.entries(MACROS).map(([k, nome]) => (
          <div key={k} className="dk-row" style={{ padding: "5px 0" }}>
            <b className="dk-num" style={{ color: "var(--primary)" }}>
              {k}
            </b>
            <span>{nome}</span>
          </div>
        ))}
      </Card>
      <Tabela colunas={colunas} linhas={SETORES} chave={(s) => s.code} />
    </div>
  );
}

/* ---------------- Versionamento ---------------- */
export function Versoes() {
  return (
    <div className="dk-page">
      <PageHeader titulo="Versionamento" sub="Evolução metodológica do instrumento" />
      <div className="dk-grid">
        {VERSOES.map((v) => (
          <Card key={v.versao}>
            <div className="dk-between" style={{ marginBottom: 6 }}>
              <div className="dk-row">
                <b style={{ fontSize: 16 }}>{v.versao}</b>
                <span className="spec">{v.data}</span>
              </div>
              <span
                className="m-pill"
                style={{
                  background: v.status === "vigente" ? "var(--mat-3-soft)" : "var(--surface-2)",
                  color: v.status === "vigente" ? "var(--mat-4)" : "var(--text-muted)",
                }}
              >
                {v.status}
              </span>
            </div>
            <div style={{ color: "var(--text-muted)", marginBottom: 6 }}>{v.resumo}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--text-muted)" }}>
              {v.mudancas.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ---------------- Monitoramento (QACI & IMS) — referência honesta ---------------- */
export function Monitoramento() {
  return (
    <div className="dk-page">
      <PageHeader titulo="QACI & IMS" sub="Monitoramento contínuo (por ciclo)" />
      <Card titulo="Onde estão os valores reais">
        <p style={{ fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          O <b>QACI</b> e o <b>IMS</b> do ciclo ativo aparecem no <b>Início</b> e no <b>Painel da Unidade</b>{" "}
          (valores reais, revalidados por ciclo). Os resultados do formulário público (IMGR) ficam em{" "}
          <b>Ciclos → Formulário &amp; IMGR</b>.
        </p>
      </Card>
      <Card titulo="IMS — dimensões avaliadas" style={{ marginTop: 16 }}>
        {DIMENSOES_IMS.map((d) => (
          <div
            key={d.code}
            className="dk-row"
            style={{ padding: "8px 0", borderBottom: "1px solid var(--slate-100)" }}
          >
            <b className="dk-num" style={{ color: "var(--primary)", width: 36 }}>
              {d.code}
            </b>
            <span style={{ flex: 1 }}>{d.nome}</span>
          </div>
        ))}
      </Card>
      <p className="spec" style={{ marginTop: 16 }}>
        O detalhamento mensal do QACI (C1/C2/C3 por mês) por ciclo ainda não é coletado — recurso em
        desenvolvimento.
      </p>
    </div>
  );
}

/* ---------------- Percepção de Risco ---------------- */
export function Percepcao() {
  const colunas: Coluna<(typeof ITENS_PERCEPCAO)[number]>[] = [
    { rotulo: "#", largura: "48px", num: true, celula: (p) => p.ordem },
    { rotulo: "Evento percebido", celula: (p) => p.desc },
    { rotulo: "P", largura: "60px", num: true, celula: (p) => p.p },
    { rotulo: "I", largura: "60px", num: true, celula: (p) => p.i },
    { rotulo: "Convergência", largura: "120px", celula: (p) => <span className="spec">{p.conv}</span> },
  ];
  return (
    <div className="dk-page">
      <PageHeader titulo="Percepção de Risco" sub="Aba 16 — leitura multivocal (20 itens)" />
      <Tabela colunas={colunas} linhas={ITENS_PERCEPCAO} chave={(p) => String(p.ordem)} />
    </div>
  );
}

/* ---------------- Supervisão AUDIN ---------------- */
export function Supervisao() {
  const cor = (r?: string) =>
    r === "C" ? "var(--mat-4)" : r === "NC" ? "var(--danger)" : "var(--text-muted)";
  const grupos: [string, readonly { id: string; criterio: string; res?: string; resultado?: string }[]][] = [
    ["Checklist geral", SUP_CHECKLIST],
    ["Pré-visita", SUP_PRE],
    ["Campo", SUP_CAMPO],
  ];
  return (
    <div className="dk-page">
      <PageHeader titulo="Supervisão AUDIN" sub="Validação independente" />
      <div className="dk-grid">
        {grupos.map(([nome, lista]) => (
          <Card key={nome} titulo={nome}>
            {lista.map((c) => {
              const r = c.res ?? c.resultado;
              return (
                <div
                  key={c.id}
                  className="dk-row"
                  style={{ padding: "7px 0", borderBottom: "1px solid var(--slate-100)" }}
                >
                  <b className="dk-num" style={{ color: cor(r), width: 36 }}>
                    {r ?? "—"}
                  </b>
                  <span>{c.criterio}</span>
                </div>
              );
            })}
          </Card>
        ))}
      </div>
    </div>
  );
}
